import React, { useState, useEffect, useRef } from 'react';

interface LogEntry {
  timestamp: string;
  message: string;
  type: 'log' | 'error' | 'warn';
}

interface DebugOverlayProps {
  enabled?: boolean;
}

export const DebugOverlay: React.FC<DebugOverlayProps> = ({ enabled = true }) => {
  // Load logs from localStorage on mount to persist across orientation changes
  const [logs, setLogs] = useState<LogEntry[]>(() => {
    try {
      const saved = localStorage.getItem('safari-debug-logs');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });
  const [isVisible, setIsVisible] = useState(false);
  const [isMinimized, setIsMinimized] = useState(true);
  const logsRef = useRef<HTMLDivElement>(null);
  const originalConsole = useRef<{
    log: typeof console.log;
    error: typeof console.error;
    warn: typeof console.warn;
  } | null>(null);

  useEffect(() => {
    if (!enabled) return;

    // Store original console methods
    originalConsole.current = {
      log: console.log,
      error: console.error,
      warn: console.warn
    };

    // Intercept console methods
    const addLog = (message: string, type: 'log' | 'error' | 'warn') => {
      const timestamp = new Date().toLocaleTimeString();
      setLogs(prev => {
        const newLogs = [...prev, { timestamp, message, type }];
        // Persist to localStorage to survive orientation changes
        try {
          localStorage.setItem('safari-debug-logs', JSON.stringify(newLogs));
        } catch (err) {
          console.error('Failed to save debug logs to localStorage:', err);
        }
        return newLogs;
      });
    };

    // Add session separator (only if this is truly a new session)
    try {
      const existingLogs = localStorage.getItem('safari-debug-logs');
      const parsedLogs = existingLogs ? JSON.parse(existingLogs) : [];
      const lastLog = parsedLogs[parsedLogs.length - 1];
      if (!lastLog || !lastLog.message.includes('NEW DEBUG SESSION STARTED')) {
        addLog('🚀 === NEW DEBUG SESSION STARTED ===', 'log');
      }
    } catch {
      addLog('🚀 === NEW DEBUG SESSION STARTED ===', 'log');
    }

    console.log = (...args: any[]) => {
      originalConsole.current?.log(...args);
      addLog(args.map(arg => typeof arg === 'object' ? JSON.stringify(arg) : String(arg)).join(' '), 'log');
    };

    console.error = (...args: any[]) => {
      originalConsole.current?.error(...args);
      addLog(args.map(arg => typeof arg === 'object' ? JSON.stringify(arg) : String(arg)).join(' '), 'error');
    };

    console.warn = (...args: any[]) => {
      originalConsole.current?.warn(...args);
      addLog(args.map(arg => typeof arg === 'object' ? JSON.stringify(arg) : String(arg)).join(' '), 'warn');
    };

    // Cleanup on unmount
    return () => {
      if (originalConsole.current) {
        console.log = originalConsole.current.log;
        console.error = originalConsole.current.error;
        console.warn = originalConsole.current.warn;
      }
    };
  }, [enabled]);

  // Auto-scroll to bottom when new logs arrive
  useEffect(() => {
    if (logsRef.current && !isMinimized) {
      logsRef.current.scrollTop = logsRef.current.scrollHeight;
    }
  }, [logs, isMinimized]);

  const copyLogs = async () => {
    const logText = logs.map(log => `[${log.timestamp}] ${log.type.toUpperCase()}: ${log.message}`).join('\n');
    try {
      await navigator.clipboard.writeText(logText);
      alert('Logs copied to clipboard!');
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = logText;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      alert('Logs copied to clipboard!');
    }
  };

  const clearLogs = () => {
    setLogs([]);
    // Also clear localStorage
    try {
      localStorage.removeItem('safari-debug-logs');
    } catch (err) {
      console.error('Failed to clear debug logs from localStorage:', err);
    }
  };

  if (!enabled) return null;

  return (
    <>
      {/* Toggle Button */}
      {!isVisible && (
        <button
          onClick={() => setIsVisible(true)}
          style={{
            position: 'fixed',
            top: '10px',
            right: '10px',
            zIndex: 10000,
            background: '#333',
            color: 'white',
            border: 'none',
            borderRadius: '50%',
            width: '50px',
            height: '50px',
            fontSize: '16px',
            cursor: 'pointer',
            boxShadow: '0 2px 10px rgba(0,0,0,0.3)'
          }}
        >
          🐛
        </button>
      )}

      {/* Debug Overlay */}
      {isVisible && (
        <div
          style={{
            position: 'fixed',
            top: '10px',
            right: '10px',
            width: isMinimized ? '300px' : '90vw',
            height: isMinimized ? '40px' : '60vh',
            maxWidth: '600px',
            background: 'rgba(0, 0, 0, 0.9)',
            color: 'white',
            borderRadius: '8px',
            zIndex: 10000,
            fontFamily: 'monospace',
            fontSize: '12px',
            overflow: 'hidden',
            boxShadow: '0 4px 20px rgba(0,0,0,0.5)',
            border: '1px solid #555'
          }}
        >
          {/* Header */}
          <div
            style={{
              padding: '8px 12px',
              background: '#444',
              borderBottom: '1px solid #666',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              cursor: 'pointer'
            }}
            onClick={() => setIsMinimized(!isMinimized)}
          >
            <span>🐛 Safari Debug ({logs.length} logs)</span>
            <div style={{ display: 'flex', gap: '8px' }}>
              {!isMinimized && (
                <>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      copyLogs();
                    }}
                    style={{
                      background: '#007acc',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      padding: '4px 8px',
                      fontSize: '10px',
                      cursor: 'pointer'
                    }}
                  >
                    Copy
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      clearLogs();
                    }}
                    style={{
                      background: '#dc3545',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      padding: '4px 8px',
                      fontSize: '10px',
                      cursor: 'pointer'
                    }}
                  >
                    Clear
                  </button>
                </>
              )}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setIsVisible(false);
                }}
                style={{
                  background: 'transparent',
                  color: 'white',
                  border: 'none',
                  fontSize: '16px',
                  cursor: 'pointer',
                  padding: '0 4px'
                }}
              >
                ✕
              </button>
            </div>
          </div>

          {/* Logs Content */}
          {!isMinimized && (
            <div
              ref={logsRef}
              style={{
                padding: '8px',
                height: 'calc(100% - 50px)',
                overflowY: 'auto',
                lineHeight: '1.4'
              }}
            >
              {logs.length === 0 ? (
                <div style={{ color: '#888', fontStyle: 'italic' }}>No logs yet...</div>
              ) : (
                logs.map((log, index) => (
                  <div
                    key={index}
                    style={{
                      marginBottom: '6px',
                      color: log.type === 'error' ? '#ff6b6b' : log.type === 'warn' ? '#ffa726' : '#e0e0e0',
                      borderLeft: `3px solid ${log.type === 'error' ? '#ff6b6b' : log.type === 'warn' ? '#ffa726' : '#4caf50'}`,
                      paddingLeft: '8px',
                      paddingTop: '2px',
                      paddingBottom: '2px',
                      backgroundColor: log.message.includes('=== NEW DEBUG SESSION STARTED ===') ? 'rgba(76, 175, 80, 0.1)' : 'transparent',
                      wordBreak: 'break-word',
                      fontSize: log.message.includes('=== NEW DEBUG SESSION STARTED ===') ? '11px' : '12px',
                      fontWeight: log.message.includes('=== NEW DEBUG SESSION STARTED ===') ? 'bold' : 'normal'
                    }}
                  >
                    <span style={{ color: '#888', fontSize: '10px' }}>[{log.timestamp}]</span>{' '}
                    <span style={{ fontWeight: 'bold', textTransform: 'uppercase', fontSize: '9px' }}>{log.type}:</span>{' '}
                    {log.message}
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      )}
    </>
  );
};